import Swiper from 'swiper';
import L from 'leaflet';
import { Navigation, Grid, Pagination, Autoplay } from 'swiper/modules';


// ── Modal ────────────────────────────────────────────────────────────────────
function initModal() {
    const modal = document.getElementById('appModal');
    const content = document.getElementById('appModalContent');
    const closeBtn = document.getElementById('appModalClose');

    if (!modal) return;

    window.openModal = function (html) {
        content.innerHTML = html;
        modal.showModal();
    };

    window.closeModal = function () {
        modal.close();
        content.innerHTML = '';
    };

    window.openImageModal = function (images = []) {
        if (!images.length) return;

        const slidesHTML = images
            .map((src) => `<div class="swiper-slide"><img src="${src}" alt="" class="modal__img" loading="lazy"></div>`)
            .join('');

        const showNav = images.length > 1
            ? `<button class="modal__nav modal__nav--prev" id="modalPrev" aria-label="Previous image">
                   <span class="iconify" data-icon="solar:alt-arrow-left-outline" data-width="28" data-height="28"></span>
               </button>
               <button class="modal__nav modal__nav--next" id="modalNext" aria-label="Next image">
                   <span class="iconify" data-icon="solar:alt-arrow-right-outline" data-width="28" data-height="28"></span>
               </button>`
            : '';

        openModal(`
            <div class="swiper modal__swiper" id="modalSwiper">
                <div class="swiper-wrapper">${slidesHTML}</div>
                ${showNav}
            </div>
        `);

        if (window.Iconify) Iconify.scan(content);

        const modalSwiper = new Swiper('#modalSwiper', { slidesPerView: 1, loop: images.length > 1 });

        if (images.length > 1) {
            document.getElementById('modalPrev').onclick = () => modalSwiper.slidePrev();
            document.getElementById('modalNext').onclick = () => modalSwiper.slideNext();
        }
    };

    // Close on X button
    closeBtn.addEventListener('click', () => window.closeModal());

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) window.closeModal();
    });
}

// ── Collection Swiper + Category Filter ───────────────────────────────────
function initCollection() {
    const section = document.querySelector('.collection');
    if (!section) return;

    const swiperEl = section.querySelector('.collection__swiper');
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    const footer = section.querySelector('.collection__footer');
    const progressFill = section.querySelector('.collection__progress-fill');
    const prevBtn = section.querySelector('.collection__nav-btn--prev');
    const nextBtn = section.querySelector('.collection__nav-btn--next');
    const catBtns = section.querySelectorAll('.collection__cat');

    // Store all original slides
    const allSlides = Array.from(wrapper.querySelectorAll('.swiper-slide'));

    let swiper = null;
    let activeSlug = 'all';
    let currentMode = null; // 'swiper' | 'grid'

    // ── Progress helper ──────────────────────────────────────────────────────
    function updateProgress() {
        if (!swiper || !progressFill) return;
        progressFill.style.width = Math.round((swiper.progress || 0) * 100) + '%';
    }

    // ── Get filtered slide set for current category ──────────────────────────
    function getFiltered(slug) {
        if (slug === 'all') return allSlides;
        return allSlides.filter(slide => {
            const cats = (slide.dataset.categories || '').split(' ').filter(Boolean);
            return cats.includes(slug);
        });
    }

    // ── SWIPER MODE (≤ 991px) ───────────────────────────────────────────────
    function buildSwiper() {
        if (swiper) {
            swiper.destroy(true, true);
            swiper = null;
        }

        // Restore swiper wrapper class & populate slides
        wrapper.innerHTML = '';
        getFiltered(activeSlug).forEach(slide => wrapper.appendChild(slide.cloneNode(true)));

        swiper = new Swiper(swiperEl, {
            modules: [Navigation, Grid],
            slidesPerView: 3,
            slidesPerGroup: 1,
            grid: { rows: 3, fill: 'row' },
            spaceBetween: 20,
            loop: false,
            navigation: {
                prevEl: prevBtn,
                nextEl: nextBtn,
            },
            breakpoints: {
                768: {
                    slidesPerView: 4,
                    slidesPerGroup: 2,
                    spaceBetween: 30,
                    grid: { rows: 3, fill: 'row' },
                },
            },
            on: {
                progress: updateProgress,
                afterInit: updateProgress,
            },
        });

        wireCardClicks();
        updateProgress();
    }

    // ── GRID MODE (desktop ≥ 992px) ─────────────────────────────────────────
    function buildGrid() {
        if (swiper) {
            swiper.destroy(true, true);
            swiper = null;
        }

        // Populate the wrapper with flat card divs (no swiper-slide class needed
        // for layout, but keep the element so filterCategory reuse works)
        wrapper.innerHTML = '';
        getFiltered(activeSlug).forEach(slide => wrapper.appendChild(slide.cloneNode(true)));

        wireCardClicks();
    }

    // ── Mode selector ────────────────────────────────────────────────────────
    function isDesktop() {
        return window.innerWidth >= 992;
    }

    function applyMode(force) {
        const desktop = isDesktop();
        const nextMode = desktop ? 'grid' : 'swiper';

        if (!force && nextMode === currentMode) return;
        currentMode = nextMode;

        if (desktop) {
            if (footer) footer.style.display = 'none';
            buildGrid();
        } else {
            if (footer) footer.style.display = '';
            buildSwiper();
        }
    }

    // ── Category filter ──────────────────────────────────────────────────────
    function filterCategory(slug) {
        activeSlug = slug;
        applyMode(true); // rebuild with new filter in current mode
    }

    // Category button clicks
    catBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            catBtns.forEach(b => b.classList.remove('collection__cat--active'));
            btn.classList.add('collection__cat--active');
            filterCategory(btn.dataset.category || 'all');
        });
    });

    // ── Resize listener ──────────────────────────────────────────────────────
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => applyMode(false), 200);
    });

    // ── Initial render ───────────────────────────────────────────────────────
    applyMode(true);
}

// ── Collection card → image modal ────────────────────────────────────────────
function wireCardClicks() {
    document.querySelectorAll('.collection__card[data-images]').forEach(card => {
        // Remove previous listener by replacing node clone
        const fresh = card.cloneNode(true);
        card.replaceWith(fresh);

        fresh.addEventListener('click', () => {
            let images = [];
            try { images = JSON.parse(fresh.dataset.images || '[]'); } catch (_) { }
            if (images.length && window.openImageModal) {
                window.openImageModal(images);
            }
        });
    });
}

// ── Hero Swiper ──────────────────────────────────────────────────────────────
function initHeroSwiper() {
    const el = document.querySelector('.hero__swiper');
    if (!el) return;

    new Swiper(el, {
        modules: [Pagination, Autoplay],
        loop: true,
        speed: 800,
        autoplay: { delay: 5000, disableOnInteraction: false },
        pagination: {
            el: '.hero__pagination',
            clickable: true,
            bulletClass: 'hero__bullet',
            bulletActiveClass: 'hero__bullet--active',
        },
    });
}

// ── Stories Swiper ───────────────────────────────────────────────────────────
function initStories() {
    const section = document.querySelector('.stories');
    if (!section) return;

    const swiperEl = section.querySelector('.stories__swiper');
    const progressFill = section.querySelector('.stories__progress-fill');
    const prevBtn = section.querySelector('.stories__nav-btn--prev');
    const nextBtn = section.querySelector('.stories__nav-btn--next');

    if (!swiperEl) return;

    function updateProgress(sw) {
        if (progressFill) progressFill.style.width = Math.round((sw.progress || 0) * 100) + '%';
    }

    new Swiper(swiperEl, {
        modules: [Navigation],
        slidesPerView: 2,
        spaceBetween: 16,
        loop: false,
        navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn,
        },
        breakpoints: {
            768: {
                slidesPerView: 2,
                spaceBetween: 16,
            },
            1200: {
                slidesPerView: 3,
                spaceBetween: 24,
            },
        },
        on: {
            progress: (sw) => updateProgress(sw),
            afterInit: (sw) => updateProgress(sw),
        },
    });
}

// ── Find Us ───────────────────────────────────────────────────────────────────
function initFindUs() {
    const mapEl = document.getElementById('home-map');
    if (!mapEl) return;

    const i18n = {
        contact: mapEl.dataset.contact || 'Contact',
        direction: mapEl.dataset.direction || 'Direction',
        open: mapEl.dataset.open || 'Open',
        closed: mapEl.dataset.closed || 'Closed',
        close: mapEl.dataset.close || 'Close',
    };

    function esc(str) {
        const d = document.createElement('div');
        d.textContent = str ?? '';
        return d.innerHTML;
    }

    const MARKER_SVG = `

<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
<ellipse cx="40.5" cy="72" rx="30.5" ry="4" fill="#3D3D3D"/>
<path d="M20.4665 15.4933C25.6737 10.3648 32.6911 7.49318 39.9998 7.50001C47.3198 7.50001 54.3465 10.37 59.5331 15.4933C64.2298 20.1267 66.6765 25.3133 67.3231 30.7C67.9665 36.04 66.8165 41.4 64.6231 46.4267C60.2698 56.4267 51.6165 65.52 43.3165 71.4367C42.3491 72.1288 41.1893 72.501 39.9998 72.501C38.8103 72.501 37.6506 72.1288 36.6831 71.4367C28.3831 65.52 19.7298 56.4233 15.3731 46.4267C13.1831 41.4 12.0365 36.04 12.6765 30.6967C13.3231 25.3133 15.7698 20.13 20.4665 15.4933ZM30.8331 33.3333C30.8331 30.9022 31.7989 28.5706 33.518 26.8515C35.2371 25.1324 37.5687 24.1667 39.9998 24.1667C42.431 24.1667 44.7625 25.1324 46.4816 26.8515C48.2007 28.5706 49.1665 30.9022 49.1665 33.3333C49.1665 35.7645 48.2007 38.0961 46.4816 39.8152C44.7625 41.5342 42.431 42.5 39.9998 42.5C37.5687 42.5 35.2371 41.5342 33.518 39.8152C31.7989 38.0961 30.8331 35.7645 30.8331 33.3333Z" fill="#921B1B"/>
<path d="M39.9886 22C40.0693 22.0719 40.4213 22.6171 40.5 22.7347C40.8365 23.2381 41.1766 23.7389 41.5203 24.2373C42.0185 24.962 42.5208 25.6839 43.0272 26.4029C43.3294 26.8326 43.7834 27.4355 44.0322 27.8751C45.0008 29.5874 45.4422 31.7847 45.5928 33.73C46.5276 33.0937 47.548 32.5983 48.6224 32.259C49.4793 31.9883 50.196 31.8584 51.0466 31.6472C52.1944 31.3622 53.8901 30.8003 54.8312 30.0515C54.6464 31.2997 54.528 32.7477 54.4394 34C54.2832 36.2097 54.5262 38.2782 53.0947 40.1756C53.3809 40.122 53.9065 40.1165 54.2052 40.109C56.7851 40.044 57.459 40.7829 60 39.6042C59.1216 41.3771 58.106 43.0763 56.963 44.6859C56.5139 45.3169 55.8456 46.1917 55.3037 46.7477C53.4053 48.6946 50.2412 49.9897 47.5471 50C45.5874 50.0072 42.6047 49.2424 41.0055 48.0612C40.6238 47.7791 40.4414 47.4584 40.1432 47.1604L40.1255 47.1432C40.0147 47.0979 39.8953 47.0932 39.8193 47.1886C39.2442 47.9098 38.7201 48.3312 37.8558 48.7162C35.4656 49.8045 32.8042 50.1211 30.2312 49.6233C29.9568 49.569 29.5655 49.5115 29.3092 49.416C28.427 49.0869 27.4747 48.7346 26.6608 48.2614C24.5372 47.0263 23.0787 44.8905 21.7986 42.8329C21.3805 42.1615 20.9823 41.4775 20.6047 40.7818C20.4225 40.4491 20.1482 39.9515 20 39.6079C21.0239 40.0811 21.9638 40.3777 23.1079 40.2645C23.5289 40.2229 23.9532 40.1713 24.3749 40.1436C25.214 40.0893 26.0559 40.0959 26.894 40.1631C25.5604 38.2582 25.7125 36.7942 25.5911 34.5955C25.5368 33.5838 25.4553 32.5737 25.3465 31.5665C25.2947 31.0857 25.2392 30.5147 25.1514 30.0449C26.1803 30.8659 27.9494 31.4019 29.2046 31.7123C29.7105 31.8374 30.2347 31.9323 30.7427 32.0736C31.9318 32.4042 33.4007 32.9829 34.3962 33.736C34.5964 31.8678 34.8808 30.0134 35.7281 28.3173C36.1761 27.4205 36.8605 26.5547 37.439 25.7309C38.2996 24.4949 39.1495 23.2512 39.9886 22ZM51.896 34.2898C51.898 34.2172 51.9107 34.1083 51.8861 34.0478C51.734 34.0219 51.282 34.1241 51.1167 34.1604C47.3715 34.9855 44.1183 37.646 42.1906 40.9675C41.3066 42.4908 40.4989 44.2339 40.2029 45.9867C40.1816 46.1135 40.1264 46.4995 39.9586 46.4733C39.8938 46.4202 39.8293 46.3054 39.8188 46.2263C39.6997 45.3294 39.4946 44.6355 39.1726 43.7943C37.7013 39.9518 34.9819 36.6053 31.2098 34.9384C30.3101 34.5364 29.0713 34.1169 28.0814 34.0379C28.2091 36.4525 28.6896 38.9544 30.0914 40.9773C30.5737 41.6732 31.3285 42.4194 31.9504 42.9865C33.7208 44.6009 35.8827 45.8788 38.2076 46.4438C38.8806 46.6072 39.5882 46.7295 40.281 46.6836C40.8569 46.6454 41.4563 46.529 42.0163 46.3811C44.856 45.6303 47.4429 43.8272 49.3903 41.6285C50.7795 40.0602 51.4802 37.8175 51.7635 35.7681C51.8145 35.3981 51.9029 34.6615 51.896 34.2898ZM52.3016 42.5633C51.3104 42.7847 51.1789 43.1786 50.5307 43.8932C50.0776 44.3925 49.5037 45.0019 48.973 45.4235C47.4384 46.6431 45.4261 47.1832 43.5218 47.4136C43.06 47.4693 42.5683 47.4266 42.1295 47.5082C43.1254 47.7584 44.2848 47.8448 45.3068 47.9166C48.524 47.9909 50.7401 47.5498 53.1274 45.2351C53.7778 44.6139 54.3859 43.9488 54.9484 43.2447C55.0728 43.089 55.3337 42.7685 55.4313 42.6008C54.8804 42.5847 54.3298 42.5719 53.7787 42.5622C53.3254 42.5522 52.7468 42.5338 52.3016 42.5633ZM37.794 47.4991L37.7575 47.4939C37.3179 47.432 36.8476 47.4741 36.3951 47.4118C34.2381 47.1161 32.0044 46.4854 30.4104 44.8928C30.3373 44.8196 30.2478 44.7296 30.1716 44.6612C29.6442 44.1875 29.1292 43.3774 28.5951 42.954C28.4138 42.8099 28.2072 42.7019 27.9863 42.6363C27.5188 42.499 26.4791 42.5535 25.9534 42.5697C25.6229 42.5753 24.8511 42.5681 24.5615 42.6081C25.7033 44.1445 27.0558 45.606 28.6514 46.6636C29.5857 47.2829 30.7601 47.6221 31.8536 47.7828C33.0966 47.9534 34.3548 47.9771 35.6032 47.8532C35.7802 47.8359 36.0712 47.8171 36.2339 47.7903C36.749 47.7325 37.3255 47.7043 37.794 47.4991ZM43.8404 35.0295C43.7237 33.8632 43.4789 32.8975 43.109 31.7878C42.5824 30.2071 41.8809 28.7569 40.7925 27.4943C40.645 27.3233 40.1501 26.7195 39.9842 26.6402C38.3336 28.2645 37.3992 30.0679 36.7265 32.2748C36.4582 33.1551 36.2127 34.1005 36.1624 35.0243L36.1772 35.0425C36.3297 35.2281 36.555 35.453 36.7227 35.6329C37.0887 36.019 37.4434 36.4158 37.7865 36.8229C38.8219 38.0753 39.6265 40.0736 39.976 41.6585L40.0037 41.6564C40.9283 38.3853 41.5259 37.4505 43.8404 35.0295Z" fill="#F5E6CC"/>
</svg>
`;

    function createStoreIcon() {
        return L.divIcon({
            className: 'store-marker',
            html: MARKER_SVG,
            iconSize: [80, 80],
            iconAnchor: [40, 80],
            popupAnchor: [0, -40],
        });
    }

    function createUserIcon() {
        return L.divIcon({
            className: 'user-location-marker',
            html: '<span class="user-location-marker__pulse"></span><span class="user-location-marker__dot"></span>',
            iconSize: [20, 20],
            iconAnchor: [10, 10],
        });
    }

    const STORE_ICON = createStoreIcon();
    const STORE_ICON_ACTIVE = createStoreIcon();
    let activeMarker = null;
    let userMarker = null;
    let userCircle = null;

    function parseTime24h(raw) {
        if (!raw || typeof raw !== 'string') return null;
        const parts = raw.split(':');
        if (parts.length !== 2) return null;
        const h = Number(parts[0]);
        const m = Number(parts[1]);
        if (isNaN(h) || isNaN(m) || h > 23 || m > 59) return null;
        return { h, m };
    }

    function formatHours(store) {
        if (!store.open_hours || !store.close_hours) return '';
        return `${store.open_hours} - ${store.close_hours}`;
    }

    function isStoreOpen(store) {
        if (!store.open_hours || !store.close_hours) return null;
        const openParsed = parseTime24h(store.open_hours);
        const closeParsed = parseTime24h(store.close_hours);
        if (!openParsed || !closeParsed) return null;

        const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Makassar' }));
        const open = new Date(now); open.setHours(openParsed.h, openParsed.m, 0, 0);
        const close = new Date(now); close.setHours(closeParsed.h, closeParsed.m, 0, 0);
        if (close <= open) close.setDate(close.getDate() + 1);
        return now >= open && now < close;
    }

    function renderStatus(store) {
        const status = isStoreOpen(store);
        if (status === null) return '<span class="status status--unknown"></span>';
        return status
            ? `<span class="status status--open">${i18n.open}</span>`
            : `<span class="status status--closed">${i18n.closed}</span>`;
    }

    function createPopupContent(store) {
        const hasPhone = store.phone && store.phone.trim() !== '';
        return `
            <div class="store-popup">
                <button class="store-popup__close" aria-label="${esc(i18n.close)}"></button>
                <h2 class="store-popup__title">${esc(store.name)}</h2>
                <div class="store-popup__meta">
                    ${hasPhone ? `
                        <a href="tel:${esc(store.phone)}" class="store-popup__phone">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.45 22.75C16.32 22.75 15.13 22.48 13.9 21.96C12.7 21.45 11.49 20.75 10.31 19.9C9.14 19.04 8.01 18.08 6.94 17.03C5.88 15.96 4.92 14.83 4.07 13.67C3.21 12.47 2.52 11.27 2.03 10.11C1.51 8.87 1.25 7.67 1.25 6.54C1.25 5.76 1.39 5.02 1.66 4.33C1.94 3.62 2.39 2.96 3 2.39C3.77 1.63 4.65 1.25 5.59 1.25C5.98 1.25 6.38 1.34 6.72 1.5C7.11 1.68 7.44 1.95 7.68 2.31L10 5.58C10.21 5.87 10.37 6.15 10.48 6.43C10.61 6.73 10.68 7.03 10.68 7.32C10.68 7.7 10.57 8.07 10.36 8.42C10.21 8.69 9.98 8.98 9.69 9.27L9.01 9.98C9.02 10.01 9.03 10.03 9.04 10.05C9.16 10.26 9.4 10.62 9.86 11.16C10.35 11.72 10.81 12.23 11.27 12.7C11.86 13.28 12.35 13.74 12.81 14.12C13.38 14.6 13.75 14.84 13.97 14.95L13.95 15L14.68 14.28C14.99 13.97 15.29 13.74 15.58 13.59C16.13 13.25 16.83 13.19 17.53 13.48C17.79 13.59 18.07 13.74 18.37 13.95L21.69 16.31C22.06 16.56 22.33 16.88 22.49 17.26C22.64 17.64 22.71 17.99 22.71 18.34C22.71 18.82 22.6 19.3 22.39 19.75C22.18 20.2 21.92 20.59 21.59 20.95C21.02 21.58 20.4 22.03 19.68 22.32C18.99 22.6 18.24 22.75 17.45 22.75ZM5.59 2.75C5.04 2.75 4.53 2.99 4.04 3.47C3.58 3.9 3.26 4.37 3.06 4.88C2.85 5.4 2.75 5.95 2.75 6.54C2.75 7.47 2.97 8.48 3.41 9.52C3.86 10.58 4.49 11.68 5.29 12.78C6.09 13.88 7 14.95 8 15.96C9 16.95 10.08 17.87 11.19 18.68C12.27 19.47 13.38 20.11 14.48 20.57C16.19 21.3 17.79 21.47 19.11 20.92C19.62 20.71 20.07 20.39 20.48 19.93C20.71 19.68 20.89 19.41 21.04 19.09C21.16 18.84 21.22 18.58 21.22 18.32C21.22 18.16 21.19 18 21.11 17.82C21.08 17.76 21.02 17.65 20.83 17.52L17.51 15.16C17.31 15.02 17.13 14.92 16.96 14.85C16.74 14.76 16.65 14.67 16.31 14.88C16.11 14.98 15.93 15.13 15.73 15.33L14.97 16.08C14.58 16.46 13.98 16.55 13.52 16.38L13.25 16.26C12.84 16.04 12.36 15.7 11.83 15.25C11.35 14.84 10.83 14.36 10.2 13.74C9.71 13.24 9.22 12.71 8.71 12.12C8.24 11.57 7.9 11.1 7.69 10.71L7.57 10.41C7.51 10.18 7.49 10.05 7.49 9.91C7.49 9.55 7.62 9.23 7.87 8.98L8.62 8.2C8.82 8 8.97 7.81 9.07 7.64C9.15 7.51 9.18 7.4 9.18 7.3C9.18 7.22 9.15 7.1 9.1 6.98C9.03 6.82 8.92 6.64 8.78 6.45L6.46 3.17C6.36 3.03 6.24 2.93 6.09 2.86C5.93 2.79 5.76 2.75 5.59 2.75Z" fill="#ffffff"/>
                            </svg>
                            ${esc(store.phone)}
                        </a>
                    ` : ''}
                    <span class="store-popup__status">
                        ${renderStatus(store)}
                        <span class="store-popup__hours">${esc(formatHours(store))}</span>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.0307 12.5306L9.53068 20.0306C9.46099 20.1002 9.37827 20.1555 9.28722 20.1932C9.19618 20.2309 9.0986 20.2503 9.00005 20.2503C8.9015 20.2503 8.80392 20.2309 8.71288 20.1932C8.62183 20.1555 8.53911 20.1002 8.46943 20.0306C8.39974 19.9609 8.34447 19.8781 8.30676 19.7871C8.26904 19.6961 8.24963 19.5985 8.24963 19.4999C8.24963 19.4014 8.26904 19.3038 8.30676 19.2128C8.34447 19.1217 8.39974 19.039 8.46943 18.9693L15.4397 11.9999L8.46943 5.03055C8.32869 4.88982 8.24963 4.69895 8.24963 4.49993C8.24963 4.30091 8.32869 4.11003 8.46943 3.9693C8.61016 3.82857 8.80103 3.74951 9.00005 3.74951C9.19907 3.74951 9.38995 3.82857 9.53068 3.9693L17.0307 11.4693C17.1004 11.539 17.1557 11.6217 17.1935 11.7127C17.2312 11.8038 17.2506 11.9014 17.2506 11.9999C17.2506 12.0985 17.2312 12.1961 17.1935 12.2871C17.1557 12.3782 17.1004 12.4609 17.0307 12.5306Z" fill="#ffffff"/>
                        </svg>
                    </span>
                </div>
                <div class="store-popup__actions">
                    ${hasPhone ? `
                        <a href="tel:${esc(store.phone)}" class="store-popup__btn">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.7741 15.0284L16.3453 13.0437C16.1447 12.9578 15.9257 12.9232 15.7083 12.9432C15.4909 12.9631 15.282 13.037 15.1003 13.1581C15.0819 13.1699 15.0643 13.1831 15.0478 13.1975L12.7331 15.1662C12.7091 15.1793 12.6824 15.1867 12.655 15.1876C12.6277 15.1886 12.6005 15.1832 12.5756 15.1719C11.0878 14.4537 9.54657 12.9219 8.82564 11.4547C8.81363 11.4302 8.80739 11.4032 8.80739 11.3759C8.80739 11.3486 8.81363 11.3217 8.82564 11.2972L10.8009 8.95342C10.8151 8.93608 10.8283 8.91792 10.8403 8.89905C10.9597 8.71671 11.0319 8.5075 11.0502 8.2903C11.0685 8.0731 11.0325 7.85476 10.9453 7.65499L8.9747 3.23374C8.86279 2.97274 8.66925 2.75509 8.42313 2.61343C8.17701 2.47178 7.89159 2.41377 7.6097 2.44811C6.3846 2.60917 5.26006 3.2108 4.44622 4.14058C3.63238 5.07036 3.18494 6.26466 3.18751 7.5003C3.18751 14.8409 9.15939 20.8128 16.5 20.8128C17.7356 20.8152 18.9298 20.3677 19.8595 19.5539C20.7893 18.7401 21.3909 17.6156 21.5522 16.3906C21.5865 16.11 21.5292 15.8259 21.389 15.5805C21.2488 15.335 21.0332 15.1414 20.7741 15.0284ZM16.5 19.6878C9.78001 19.6878 4.31251 14.2203 4.31251 7.5003C4.30938 6.53836 4.65658 5.60818 5.28924 4.88356C5.9219 4.15894 6.79675 3.68944 7.75032 3.5628H7.77189C7.80967 3.5635 7.84635 3.57561 7.87713 3.59752C7.90791 3.61944 7.93136 3.65014 7.94439 3.68561L9.92251 8.10217C9.93379 8.12673 9.93963 8.15343 9.93963 8.18045C9.93963 8.20748 9.93379 8.23418 9.92251 8.25873L7.94345 10.6081C7.92868 10.6249 7.91521 10.6427 7.90314 10.6615C7.7793 10.8506 7.70637 11.0684 7.69143 11.2939C7.67648 11.5194 7.72002 11.745 7.81782 11.9487C8.64939 13.6512 10.365 15.3537 12.0863 16.1853C12.2912 16.2826 12.5178 16.3251 12.7441 16.3086C12.9703 16.2922 13.1885 16.2174 13.3772 16.0915C13.395 16.0794 13.4128 16.0662 13.4297 16.0522L15.7434 14.0834C15.7663 14.0711 15.7915 14.0639 15.8174 14.0623C15.8433 14.0607 15.8692 14.0647 15.8934 14.074L20.3231 16.0587C20.3593 16.0741 20.3897 16.1005 20.4101 16.1342C20.4305 16.1678 20.4397 16.207 20.4366 16.2462C20.3106 17.2002 19.8415 18.0757 19.1171 18.7091C18.3926 19.3425 17.4623 19.6904 16.5 19.6878Z" fill="#1a1a1a"/>
                            </svg>
                            ${i18n.contact}
                        </a>
                    ` : ''}
                    <a href="https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}"
                       target="_blank"
                       rel="noopener noreferrer"
                       class="store-popup__btn">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.1198 21.75C17.5198 21.75 16.9098 21.61 16.3298 21.32L13.0898 19.7C12.4098 19.36 11.5898 19.36 10.8998 19.7L7.65985 21.32C6.09985 22.1 4.29985 21.8 3.07985 20.55C1.85985 19.3 1.58985 17.5 2.40985 15.95L8.50985 4.36C9.18985 3.06 10.5298 2.25 11.9998 2.25C13.4698 2.25 14.8098 3.06 15.4898 4.36L21.5898 15.95C22.3998 17.5 22.1398 19.3 20.9198 20.55C20.1498 21.34 19.1498 21.75 18.1198 21.75ZM11.9998 17.94C12.6098 17.94 13.2098 18.08 13.7598 18.35L16.9998 19.97C18.1698 20.56 19.2398 20.11 19.8398 19.49C20.4398 18.87 20.8698 17.8 20.2598 16.64L14.1598 5.05C13.7298 4.24 12.9198 3.75 11.9998 3.75C11.0798 3.75 10.2698 4.24 9.83985 5.06L3.73985 16.65C3.12985 17.81 3.54985 18.89 4.15985 19.5C4.76985 20.11 5.82985 20.56 6.99985 19.98L10.2398 18.36C10.7898 18.08 11.3898 17.94 11.9998 17.94Z" fill="#1A1A1A"/>
                        </svg>
                        ${i18n.direction}
                    </a>
                </div>
            </div>
        `;
    }

    function renderList(map, markers) {
        const list = document.querySelector('.store-locator__store-list');
        if (!list) return;

        list.innerHTML = '';

        const stores = (window.HOME_STORES || [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));

        stores.forEach(store => {
            const item = document.createElement('div');
            item.className = 'store-locator__store-item';
            item.dataset.storeId = store.id;
            const hasPhone = store.phone && store.phone.trim() !== '';

            item.innerHTML = `
                <div class="store-locator__store-header">
                    <h2 class="store-locator__store-name">${esc(store.name)}</h2>
                </div>
                <p class="store-locator__store-address">${esc(store.address)}</p>
                <div class="store-locator__store-info">
                    ${hasPhone ? `
                        <a class="store-locator__call-wrapper" href="tel:${esc(store.phone)}">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.7741 15.0284L16.3453 13.0437C16.1447 12.9578 15.9257 12.9232 15.7083 12.9432C15.4909 12.9631 15.282 13.037 15.1003 13.1581C15.0819 13.1699 15.0643 13.1831 15.0478 13.1975L12.7331 15.1662C12.7091 15.1793 12.6824 15.1867 12.655 15.1876C12.6277 15.1886 12.6005 15.1832 12.5756 15.1719C11.0878 14.4537 9.54657 12.9219 8.82564 11.4547C8.81363 11.4302 8.80739 11.4032 8.80739 11.3759C8.80739 11.3486 8.81363 11.3217 8.82564 11.2972L10.8009 8.95342C10.8151 8.93608 10.8283 8.91792 10.8403 8.89905C10.9597 8.71671 11.0319 8.5075 11.0502 8.2903C11.0685 8.0731 11.0325 7.85476 10.9453 7.65499L8.9747 3.23374C8.86279 2.97274 8.66925 2.75509 8.42313 2.61343C8.17701 2.47178 7.89159 2.41377 7.6097 2.44811C6.3846 2.60917 5.26006 3.2108 4.44622 4.14058C3.63238 5.07036 3.18494 6.26466 3.18751 7.5003C3.18751 14.8409 9.15939 20.8128 16.5 20.8128C17.7356 20.8152 18.9298 20.3677 19.8595 19.5539C20.7893 18.7401 21.3909 17.6156 21.5522 16.3906C21.5865 16.11 21.5292 15.8259 21.389 15.5805C21.2488 15.335 21.0332 15.1414 20.7741 15.0284ZM16.5 19.6878C9.78001 19.6878 4.31251 14.2203 4.31251 7.5003C4.30938 6.53836 4.65658 5.60818 5.28924 4.88356C5.9219 4.15894 6.79675 3.68944 7.75032 3.5628H7.77189C7.80967 3.5635 7.84635 3.57561 7.87713 3.59752C7.90791 3.61944 7.93136 3.65014 7.94439 3.68561L9.92251 8.10217C9.93379 8.12673 9.93963 8.15343 9.93963 8.18045C9.93963 8.20748 9.93379 8.23418 9.92251 8.25873L7.94345 10.6081C7.92868 10.6249 7.91521 10.6427 7.90314 10.6615C7.7793 10.8506 7.70637 11.0684 7.69143 11.2939C7.67648 11.5194 7.72002 11.745 7.81782 11.9487C8.64939 13.6512 10.365 15.3537 12.0863 16.1853C12.2912 16.2826 12.5178 16.3251 12.7441 16.3086C12.9703 16.2922 13.1885 16.2174 13.3772 16.0915C13.395 16.0794 13.4128 16.0662 13.4297 16.0522L15.7434 14.0834C15.7663 14.0711 15.7915 14.0639 15.8174 14.0623C15.8433 14.0607 15.8692 14.0647 15.8934 14.074L20.3231 16.0587C20.3593 16.0741 20.3897 16.1005 20.4101 16.1342C20.4305 16.1678 20.4397 16.207 20.4366 16.2462C20.3106 17.2002 19.8415 18.0757 19.1171 18.7091C18.3926 19.3425 17.4623 19.6904 16.5 19.6878Z" fill="#171717"/>
                            </svg>
                            <span class="store-locator__number">${esc(store.phone)}</span>
                        </a>
                    ` : ''}
                    <div class="store-locator__store-status">
                        ${renderStatus(store)}
                        <div class="store-locator__open-hour">
                            ${esc(formatHours(store))}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.0307 12.5306L9.53068 20.0306C9.46099 20.1002 9.37827 20.1555 9.28722 20.1932C9.19618 20.2309 9.0986 20.2503 9.00005 20.2503C8.9015 20.2503 8.80392 20.2309 8.71288 20.1932C8.62183 20.1555 8.53911 20.1002 8.46943 20.0306C8.39974 19.9609 8.34447 19.8781 8.30676 19.7871C8.26904 19.6961 8.24963 19.5985 8.24963 19.4999C8.24963 19.4014 8.26904 19.3038 8.30676 19.2128C8.34447 19.1217 8.39974 19.039 8.46943 18.9693L15.4397 11.9999L8.46943 5.03055C8.32869 4.88982 8.24963 4.69895 8.24963 4.49993C8.24963 4.30091 8.32869 4.11003 8.46943 3.9693C8.61016 3.82857 8.80103 3.74951 9.00005 3.74951C9.19907 3.74951 9.38995 3.82857 9.53068 3.9693L17.0307 11.4693C17.1004 11.539 17.1557 11.6217 17.1935 11.7127C17.2312 11.8038 17.2506 11.9014 17.2506 11.9999C17.2506 12.0985 17.2312 12.1961 17.1935 12.2871C17.1557 12.3782 17.1004 12.4609 17.0307 12.5306Z" fill="currentColor"/>
                            </svg>
                        </div>
                    </div>
                </div>
            `;

            item.addEventListener('click', () => {
                const marker = markers.get(store.id);
                if (!marker) return;
                document
                    .querySelectorAll('.store-locator__store-item')
                    .forEach(el => el.classList.remove('is-active'));
                item.classList.add('is-active');
                map.flyTo(marker.getLatLng(), 13);
                marker.openPopup();
            });

            list.appendChild(item);

            const marker = markers.get(store.id);
            if (marker) marker.addTo(map);
        });
    }

    function handleMarkerPopupOpen(marker, map) {
        if (activeMarker && activeMarker !== marker) {
            activeMarker.setIcon(STORE_ICON);
        }
        marker.setIcon(STORE_ICON_ACTIVE);
        activeMarker = marker;

        document
            .querySelectorAll('.store-locator__store-item')
            .forEach(el => el.classList.remove('is-active'));

        const storeItem = document.querySelector(
            `.store-locator__store-item[data-store-id="${marker.storeId}"]`
        );
        if (storeItem) {
            storeItem.classList.add('is-active');
            storeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        const targetPoint = map.project(marker.getLatLng(), 16);
        targetPoint.y -= 150;
        map.flyTo(map.unproject(targetPoint, 16), 16, { duration: 1, easeLinearity: 0.25 });
    }

    function handleMarkerPopupClose(marker) {
        marker.setIcon(STORE_ICON);
        activeMarker = null;
        const storeItem = document.querySelector(
            `.store-locator__store-item[data-store-id="${marker.storeId}"]`
        );
        if (storeItem) storeItem.classList.remove('is-active');
    }

    function attachPopupCloseHandler(map) {
        map.on('popupopen', e => {
            const closeBtn = e.popup.getElement()?.querySelector('.store-popup__close');
            if (closeBtn) closeBtn.onclick = () => map.closePopup();
        });
    }

    function detectUserLocation(map) {
        if (!navigator.geolocation) return;
        map.locate({ setView: true, maxZoom: 14, enableHighAccuracy: true });
        map.on('locationfound', e => {
            const { lat, lng } = e.latlng;
            if (userCircle) map.removeLayer(userCircle);
            userCircle = L.circle([lat, lng], {
                radius: e.accuracy, color: '#1363DF', fillOpacity: 0.08, weight: 1,
            }).addTo(map);

            if (userMarker) {
                userMarker.setLatLng([lat, lng]);
            } else {
                userMarker = L.marker([lat, lng], { icon: createUserIcon(), zIndexOffset: 1000 }).addTo(map);
            }
        });
        map.on('locationerror', () => { });
    }

    // ── Boot ──────────────────────────────────────────────────────────────────
    const map = L.map('home-map').setView([-8.670458, 115.212629], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const markers = new Map();
    (window.HOME_STORES || []).forEach(store => {
        if (!store.lat || !store.lng) return;
        const marker = L.marker([store.lat, store.lng], { icon: STORE_ICON })
            .bindPopup(createPopupContent(store), {
                closeButton: false,
                className: 'store-popup-wrapper',
                offset: [0, -20],
            });
        marker.storeId = store.id;
        marker.on('popupopen', () => handleMarkerPopupOpen(marker, map));
        marker.on('popupclose', () => handleMarkerPopupClose(marker));
        markers.set(store.id, marker);
    });

    attachPopupCloseHandler(map);
    renderList(map, markers);
    detectUserLocation(map);
}

document.addEventListener('DOMContentLoaded', () => {
    initHeroSwiper();
    initModal();
    initCollection();
    initStories();
    initFindUs();
});
